import _ from 'lodash';
import { ApolloError, MutationResult } from '@apollo/client';
import React, { useState } from 'react';
import { Formik, FormikHelpers, FormikProps } from 'formik';
import * as Yup from 'yup';

// Data
import { GENDER_OPTIONS } from '../../../lib/constants';
import { dateOfBirthSchema } from '../../../lib/helpers/player.validation';

// Components
import { IEditPlayerFields } from './edit-player.interface';
import { EditPlayer } from './edit-player.component';
import { HANDEDNESS_LABELS, PrimarySport, PrimarySportLabels, SportTerminology } from '../../../lib/types';
import { useCompanySportTerminology } from '../../../lib/stores/company.store';

export interface IEditPlayerContainer {
  hasConfigRole?: boolean;
  player?: IPlayer;
  updatePlayerMutation: Function;
  fetchError: boolean;
  loading: boolean;
}

const EditPlayerContainer: React.FC<IEditPlayerContainer> = ({
  hasConfigRole,
  player,
  updatePlayerMutation,
  fetchError,
  loading,
}) => {
  const termLookup = useCompanySportTerminology();
  const athleteTerm = termLookup[SportTerminology.ATHLETE];

  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showSaved, setShowSaved] = useState(false);

  const INITIAL_FORM_VALUES = {
    firstName: player?.firstName || '',
    lastName: player?.lastName || '',
    nickname: player?.nickname || '',
    date: player?.dateOfBirth || '',
    gender: player?.gender ? { value: player?.gender, label: player?.gender } : GENDER_OPTIONS[1],
    externalId: player?.externalId || '',
    handedness: player?.handedness ? { value: player.handedness, label: HANDEDNESS_LABELS[player.handedness] } : null,
    primarySport: player?.primarySport && player.primarySport in PrimarySportLabels ? { value: player.primarySport, label: PrimarySportLabels[player.primarySport as PrimarySport] } : null,
  };

  async function handleSubmit(values: IEditPlayerFields, { setSubmitting }: FormikHelpers<IEditPlayerFields>) {
    setSubmitError(null);


    return updatePlayerMutation({
      ..._.omit(values, ['gender', 'date', 'primarySport', 'handedness']),
      dateOfBirth: values.date,
      gender: values.gender.value,
      primarySport: values.primarySport?.value,
      handedness: values.handedness?.value,
    })
      .then((result: MutationResult) => {
        setSubmitting(false);

        if (result.error) {
          setSubmitError(result.error.message);
        } else {
          setShowSaved(true);
          setTimeout(() => {
            setShowSaved(false);
          }, 2000);
        }
      })
      .catch((err: ApolloError) => setSubmitError(err.message))
  }

  const validationSchema = Yup.object().shape({
    firstName: Yup.string().required(`Enter the ${athleteTerm}'s first name`),
    lastName: Yup.string().required(`Enter the ${athleteTerm}'s last name`),
    date: dateOfBirthSchema(`Enter the ${athleteTerm}'s date of birth`),
  });

  return (
    <Formik
      enableReinitialize
      initialValues={INITIAL_FORM_VALUES as IEditPlayerFields}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {(form: FormikProps<IEditPlayerFields>) => <EditPlayer
        hasConfigRole={hasConfigRole}
        form={form}
        submitError={submitError}
        showSaved={showSaved}
        fetchError={fetchError}
        loading={loading}
      />}
    </Formik>
  );
};

export { EditPlayerContainer };
